// XSLT → Belge Dizaynı (LayoutJson) içe aktarıcı.
//
// Amaç (Talep #153): Kullanıcı bir e-belge XSLT'si yükler; tasarımı bizim sürükle-bırak
// dizayn sistemimize TAM ENTEGRE çevrilir (ayrı XSLT baskı yolu YOK):
//  • Statik metin/çizgi/kutu/dolgu/görsel → mutlak konumlu öğeler.
//  • Dinamik alanlar → bizim alan kataloğuna BAĞLI `field` öğeleri (eşlenemezse {…} yer tutucu).
//  • Tekrar eden kalem tablosu → gerçek DETAY BANDI (kolonları eşlenir).
//
// ÖNEMLİ: GİB e-Fatura/e-Arşiv XSLT'leri VERİ ODAKLIDIR — alanlar/kalemler ancak gerçek
// fatura XML'i şablona uygulanınca (apply-templates/call-template/for-each) oluşur. Bu yüzden
// XSLT'yi kanonik bir ÖRNEK UBL faturasına (xsltSampleUbl) uygularız; böylece tüm yapı
// (başlık alanları + kalem satırları + toplamlar) çıkar. value-of'lar «f:XPath» işaretçisine
// çevrilir → hangi metnin dinamik olduğunu ve XPath'ini biliriz (alan bağlama için).
// Tamamen istemci tarafı; backend/DB değişikliği yok.

import { PX_PER_MM, round } from './geometry';
import { mapHeaderField, mapDetailColumn } from './xsltFieldMap';
import { SAMPLE_UBL_INVOICE } from './xsltSampleUbl';

const XSL_NS = 'http://www.w3.org/1999/XSL/Transform';
const MAX_ELEMENTS = 1800;
const SENTINEL_OPEN = '«f:';
const SENTINEL_CLOSE = '»';
const REPEAT_ATTR = 'data-xrepeat';

function uid() {
  return window.crypto?.randomUUID?.() || `el-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function parseXml(text, label) {
  // XML bildirimi belgenin EN başında olmalı → öndeki BOM/boşlukları kırp.
  const clean = String(text || '').replace(/^﻿/, '').replace(/^\s+(?=<)/, '');
  const doc = new DOMParser().parseFromString(clean, 'application/xml');
  const err = doc.querySelector('parsererror');
  if (err) {
    const msg = (err.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 240);
    throw new Error(`${label} ayrıştırılamadı: ${msg || 'geçersiz XML'}`);
  }
  return doc;
}

function rgbToHex(rgb) {
  if (!rgb) return null;
  const m = rgb.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?/i);
  if (!m) return null;
  const a = m[4] === undefined ? 1 : parseFloat(m[4]);
  if (a === 0) return null;
  const h = (n) => Math.max(0, Math.min(255, Math.round(parseFloat(n)))).toString(16).padStart(2, '0');
  return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
}

function isNearWhite(hex) {
  if (!hex) return true;
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return r >= 246 && g >= 246 && b >= 246;
}

function sentinelXPath(text) {
  const t = (text || '').trim();
  if (t.startsWith(SENTINEL_OPEN) && t.endsWith(SENTINEL_CLOSE)) return t.slice(SENTINEL_OPEN.length, -SENTINEL_CLOSE.length).trim();
  // gömülü tek sentinel (ör. hücrede "&#160;«f:./cbc:ID»")
  const m = t.match(/«f:([^»]*)»/);
  return m && t.replace(/«f:[^»]*»/g, '').trim() === '' ? m[1].trim() : null;
}

function cleanText(s) { return (s || '').replace(/«f:[^»]*»/g, '').replace(/\s+/g, ' ').trim(); }

function firstLiteralElement(node) {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType !== 1) continue;
    if (child.namespaceURI !== XSL_NS) return child;
    const deep = firstLiteralElement(child);
    if (deep) return deep;
  }
  return null;
}

// value-of → «f:select» sentinel (XPath korunur). skeleton=true ise ek olarak koşulları açar
// ve for-each'i tek tura sabitler (örnek veri yokken iskelet için). İşaretçi satırları da etiketler.
function neutralizeStylesheet(xsltDoc, skeleton) {
  const doc = xsltDoc.cloneNode(true);
  for (const vo of Array.from(doc.getElementsByTagNameNS(XSL_NS, 'value-of'))) {
    const sel = vo.getAttribute('select') || '';
    const txt = doc.createElementNS(XSL_NS, 'xsl:text');
    txt.textContent = `${SENTINEL_OPEN}${sel}${SENTINEL_CLOSE}`;
    vo.parentNode.replaceChild(txt, vo);
  }
  // GİB deseni: <xsl:for-each select="X"><xsl:apply-templates/></xsl:for-each> ile ALAN DEĞERİ
  // basılır (value-of YOK → yerleşik metin şablonu). select'siz apply-templates'i, en yakın ata
  // for-each select'ini taşıyan sentinel'e çevir (select'li olanlar şablon çağrısıdır — dokunma).
  for (const at of Array.from(doc.getElementsByTagNameNS(XSL_NS, 'apply-templates'))) {
    if (at.getAttribute('select')) continue;
    let anc = at.parentNode; let sel = '.';
    while (anc && anc.nodeType === 1) {
      if (anc.namespaceURI === XSL_NS && anc.localName === 'for-each' && anc.getAttribute('select')) { sel = anc.getAttribute('select'); break; }
      anc = anc.parentNode;
    }
    const txt = doc.createElementNS(XSL_NS, 'xsl:text');
    txt.textContent = `${SENTINEL_OPEN}${sel}${SENTINEL_CLOSE}`;
    at.parentNode.replaceChild(txt, at);
  }
  const fes = Array.from(doc.getElementsByTagNameNS(XSL_NS, 'for-each'));
  fes.forEach((fe, idx) => {
    if (skeleton) fe.setAttribute('select', '/');
    const row = firstLiteralElement(fe);
    if (row) {
      const attr = doc.createElementNS(XSL_NS, 'xsl:attribute');
      attr.setAttribute('name', REPEAT_ATTR);
      attr.textContent = String(idx);
      row.insertBefore(attr, row.firstChild);
    }
  });
  if (skeleton) {
    for (const e of Array.from(doc.getElementsByTagNameNS(XSL_NS, 'if'))) e.setAttribute('test', 'true()');
    for (const e of Array.from(doc.getElementsByTagNameNS(XSL_NS, 'when'))) e.setAttribute('test', 'true()');
  }
  return doc;
}

function buildProcessor(stylesheetDoc) {
  if (typeof window.XSLTProcessor === 'undefined') throw new Error('Tarayıcı XSLT dönüşümünü desteklemiyor (XSLTProcessor yok).');
  const proc = new window.XSLTProcessor();
  proc.importStylesheet(stylesheetDoc);
  return proc;
}

function fragHasText(frag) {
  const d = document.createElement('div');
  d.appendChild(frag.cloneNode(true));
  return (d.textContent || '').replace(/\s+/g, '').length > 20;
}

function renderToHost(xsltText, userXml) {
  const xsltDoc = parseXml(xsltText, 'XSLT dosyası');
  const sampleXml = userXml && userXml.trim() ? userXml : SAMPLE_UBL_INVOICE;
  let fragment = null;
  let lastErr = null;
  const attempts = [
    // 1) Veri modu: örnek UBL'e karşı, value-of'lar sentinel (en dolu sonuç).
    () => buildProcessor(neutralizeStylesheet(xsltDoc, false)).transformToFragment(parseXml(sampleXml, 'Örnek XML'), document),
    // 2) Kullanıcı XML'i başarısızsa kanonik örnek UBL'e karşı.
    () => buildProcessor(neutralizeStylesheet(xsltDoc, false)).transformToFragment(parseXml(SAMPLE_UBL_INVOICE, 'iç UBL'), document),
    // 3) İskelet modu: veri sürmüyorsa sentetik köke (sadece statik iskelet).
    () => buildProcessor(neutralizeStylesheet(xsltDoc, true)).transformToFragment(parseXml('<root/>', 'iç'), document),
    () => buildProcessor(xsltDoc).transformToFragment(parseXml(SAMPLE_UBL_INVOICE, 'iç UBL'), document),
  ];
  for (const run of attempts) {
    try {
      const f = run();
      if (f && f.childNodes && f.childNodes.length && fragHasText(f)) { fragment = f; break; }
      if (f && f.childNodes && f.childNodes.length && !fragment) fragment = f; // en azından bir şey tut
    } catch (e) { lastErr = e; }
  }
  if (!fragment) throw lastErr || new Error('XSLT dönüşümü boş sonuç verdi.');

  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;background:#fff;color:#000;z-index:-1;pointer-events:none;';
  host.appendChild(fragment);
  document.body.appendChild(host);
  const body = host.querySelector('body');
  return { host, root: body || host };
}

function rectOf(node) {
  try {
    const range = document.createRange();
    if (node.nodeType === Node.TEXT_NODE) range.selectNode(node);
    else range.selectNodeContents(node);
    const r = range.getBoundingClientRect();
    return r && (r.width > 0 || r.height > 0) ? r : null;
  } catch {
    return node.getBoundingClientRect ? node.getBoundingClientRect() : null;
  }
}

function nearbyLabel(textNode) {
  const cell = textNode.parentElement?.closest('td,th,div,span,p,li') || textNode.parentElement;
  if (!cell) return '';
  let sib = cell.previousElementSibling;
  while (sib) {
    const t = cleanText(sib.textContent);
    if (t) return t.slice(0, 60);
    sib = sib.previousElementSibling;
  }
  const own = cleanText(cell.textContent);
  return own.slice(0, 60);
}

const cellsOf = (tr) => Array.from(tr.children).filter((c) => c.matches && c.matches('td,th'));
const rowHasSentinel = (tr) => /«f:/.test(tr.textContent || '');

// Tekrar eden kalem tablosunu bulur: aynı kapsayıcıda ardışık, DİNAMİK (sentinel'li), ≥3 hücreli
// en yüksek (satır×kolon) puanlı satır bloğu. Başlık = bloktan önceki sentinel'siz statik satır.
// (Statik başlık satırı veri satırlarıyla aynı hücre sayısında olduğundan ayrı tutmak şart.)
function findDetail(root) {
  const tables = Array.from(root.querySelectorAll('table'));
  let best = null;
  for (const table of tables) {
    const trs = Array.from(table.querySelectorAll(':scope > tr, :scope > thead > tr, :scope > tbody > tr'));
    let i = 0;
    while (i < trs.length) {
      const cc = cellsOf(trs[i]).length;
      if (cc >= 3 && rowHasSentinel(trs[i])) {
        let j = i;
        while (j < trs.length && cellsOf(trs[j]).length === cc && rowHasSentinel(trs[j])) j++;
        const score = (j - i) * cc;
        if (!best || score > best.score) best = { table, trs, startIdx: i, rows: trs.slice(i, j), cc, score };
        i = j;
      } else i++;
    }
  }
  let chosen = null;
  if (best) {
    const tmpl = best.rows[0];
    const idx = best.trs.indexOf(tmpl);
    let headerRow = null;
    for (let k = idx - 1; k >= 0; k--) { if (!rowHasSentinel(best.trs[k])) { headerRow = best.trs[k]; break; } }
    if (!headerRow && idx > 0) headerRow = best.trs[idx - 1];
    chosen = { table: best.table, row: tmpl, cells: cellsOf(tmpl), headerRow };
  } else {
    // Marker geri dönüşü (inline for-each, tek satır).
    const marked = Array.from(root.querySelectorAll(`[${REPEAT_ATTR}]`));
    let bm = null;
    for (const el of marked) {
      const tr = el.matches('tr') ? el : (el.querySelector('tr') || el);
      const cells = cellsOf(tr);
      const cnt = cells.length || tr.children.length;
      if (!bm || cnt > bm.cnt) bm = { tr, cells: cells.length ? cells : Array.from(tr.children), cnt };
    }
    if (bm) {
      const table = bm.tr.closest('table') || bm.tr;
      const rows = Array.from(table.querySelectorAll('tr'));
      const di = rows.indexOf(bm.tr);
      chosen = { table, row: bm.tr, cells: bm.cells, headerRow: di > 0 ? rows[di - 1] : null };
    }
  }
  if (!chosen) return null;
  chosen.headerCells = chosen.headerRow ? Array.from(chosen.headerRow.children).map((c) => cleanText(c.textContent)) : [];
  return chosen;
}

function collectRaw(root, detailTable) {
  const texts = [];
  const decos = [];
  const inDetail = (node) => detailTable && detailTable.contains(node);

  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const v = n.nodeValue;
      if (!v || !v.trim()) return NodeFilter.FILTER_REJECT;
      const pt = n.parentElement?.tagName;
      if (pt === 'SCRIPT' || pt === 'STYLE' || pt === 'HEAD') return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let tn;
  // eslint-disable-next-line no-cond-assign
  while ((tn = tw.nextNode())) {
    if (inDetail(tn)) continue;
    const rect = rectOf(tn);
    if (!rect || rect.width <= 0 || rect.height <= 0) continue;
    const cs = window.getComputedStyle(tn.parentElement);
    if (cs.visibility === 'hidden' || cs.display === 'none') continue;
    const raw = tn.nodeValue.replace(/\s+/g, ' ').trim();
    const xpath = sentinelXPath(raw);
    texts.push({ rect, cs, text: raw, xpath, label: xpath ? nearbyLabel(tn) : '' });
  }

  const ew = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, null);
  let en;
  // eslint-disable-next-line no-cond-assign
  while ((en = ew.nextNode())) {
    const tag = en.tagName;
    if (['SCRIPT', 'STYLE', 'HEAD', 'META', 'LINK', 'TITLE'].includes(tag)) continue;
    if (inDetail(en)) continue;
    const rect = en.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) continue;
    const cs = window.getComputedStyle(en);
    if (cs.visibility === 'hidden' || cs.display === 'none') continue;
    if (tag === 'IMG' && en.getAttribute('src')) { decos.push({ kind: 'image', rect, src: en.src || en.getAttribute('src') }); continue; }
    decos.push({ kind: 'box', rect, cs });
  }
  return { texts, decos };
}

function makeMapper(allRects, printWidthMm) {
  let minL = Infinity, minT = Infinity, maxR = -Infinity, maxB = -Infinity;
  for (const r of allRects) {
    minL = Math.min(minL, r.left); minT = Math.min(minT, r.top);
    maxR = Math.max(maxR, r.right); maxB = Math.max(maxB, r.bottom);
  }
  const contentW = Math.max(1, maxR - minL);
  let scale = (printWidthMm * PX_PER_MM) / contentW;
  if (!Number.isFinite(scale) || scale <= 0) scale = 1;
  scale = Math.max(0.05, Math.min(scale, 8));
  return {
    scale, minL, minT, totalArea: contentW * Math.max(1, maxB - minT),
    x: (px) => round((px - minL) * scale / PX_PER_MM),
    y: (px) => round((px - minT) * scale / PX_PER_MM),
    w: (px) => round(Math.max(1, px * scale / PX_PER_MM)),
    len: (px) => round(Math.max(0.5, px * scale / PX_PER_MM)),
    pt: (px) => round(Math.max(4, Math.min(72, px * 0.75 * scale))),
  };
}

function textElement(it, M) {
  const r = it.rect, cs = it.cs;
  const heightMm = round(Math.max(2, r.height * M.scale / PX_PER_MM));
  const fontPt = M.pt(parseFloat(cs.fontSize) || 12);
  const lineMm = fontPt * 0.3528 * 1.2;
  const align = cs.textAlign === 'right' ? 'right' : cs.textAlign === 'center' ? 'center' : 'left';
  const base = {
    id: uid(), x: M.x(r.left), y: M.y(r.top),
    width: round(Math.max(3, r.width * M.scale / PX_PER_MM + 1)), height: heightMm,
    fontSizePt: fontPt,
    bold: cs.fontWeight === 'bold' || parseInt(cs.fontWeight, 10) >= 600,
    italic: cs.fontStyle === 'italic',
    underline: (cs.textDecorationLine || cs.textDecoration || '').includes('underline'),
    align, valign: 'top', color: rgbToHex(cs.color) || '#000000',
    wrap: heightMm > lineMm * 1.6, border: false,
  };
  if (it.xpath) {
    const map = mapHeaderField(it.xpath, it.label);
    if (map) return { ...base, type: 'field', source: map.source, fieldKey: map.fieldKey, fieldType: map.fieldType };
    return { ...base, type: 'static-text', text: `{${it.xpath}}` };
  }
  const cleaned = it.text.replace(/«f:([^»]*)»/g, (_, x) => `{${x.trim()}}`);
  return { ...base, type: 'static-text', text: cleaned };
}

function decoElements(decos, M) {
  const out = [];
  for (const it of decos) {
    const r = it.rect;
    if (it.kind === 'image') {
      out.push({ id: uid(), type: 'image', src: it.src, fit: 'contain', x: M.x(r.left), y: M.y(r.top), width: M.w(r.width), height: M.len(r.height), border: false });
      continue;
    }
    const cs = it.cs;
    const bg = rgbToHex(cs.backgroundColor);
    if (bg && !isNearWhite(bg) && r.width * r.height < M.totalArea * 0.9) {
      out.push({ id: uid(), type: 'box', x: M.x(r.left), y: M.y(r.top), width: M.w(r.width), height: M.len(r.height), bgColor: bg, borderColor: bg, borderWidth: 0, radius: round((parseFloat(cs.borderTopLeftRadius) || 0) * M.scale / PX_PER_MM) });
    }
    const sides = [
      ['Top', r.left, r.top, r.width, true], ['Bottom', r.left, r.bottom, r.width, true],
      ['Left', r.left, r.top, r.height, false], ['Right', r.right, r.top, r.height, false],
    ];
    for (const [side, sx, sy, length, horizontal] of sides) {
      const bw = parseFloat(cs[`border${side}Width`]) || 0;
      const bstyle = cs[`border${side}Style`];
      if (bw <= 0 || bstyle === 'none' || bstyle === 'hidden') continue;
      const bcol = rgbToHex(cs[`border${side}Color`]) || '#000';
      const thick = round(Math.max(0.2, bw * M.scale / PX_PER_MM));
      if (horizontal) out.push({ id: uid(), type: 'line', x: M.x(sx), y: M.y(sy), width: M.w(length), height: thick, color: bcol });
      else out.push({ id: uid(), type: 'box', x: M.x(sx), y: M.y(sy), width: thick, height: M.len(length), bgColor: bcol, borderColor: bcol, borderWidth: 0 });
    }
  }
  return out;
}

function buildDetailBand(detail, M) {
  const tRect = detail.table.getBoundingClientRect();
  const headRect = detail.headerRow ? detail.headerRow.getBoundingClientRect() : detail.row.getBoundingClientRect();
  const columns = detail.cells.map((cell, i) => {
    const cr = cell.getBoundingClientRect();
    const cs = window.getComputedStyle(cell);
    const header = (detail.headerCells[i] || '').trim();
    const xpath = sentinelXPath((cell.textContent || '').trim()) || '';
    const map = mapDetailColumn(xpath, header);
    return {
      id: uid(),
      fieldKey: map ? map.fieldKey : '',
      fieldType: map ? map.fieldType : 'text',
      headerText: header,
      x: round(Math.max(0, (cr.left - tRect.left) * M.scale / PX_PER_MM)),
      width: round(Math.max(6, cr.width * M.scale / PX_PER_MM)),
      fontSizePt: M.pt(parseFloat(cs.fontSize) || 11),
      bold: cs.fontWeight === 'bold' || parseInt(cs.fontWeight, 10) >= 600,
      underline: false,
      align: cs.textAlign === 'right' ? 'right' : cs.textAlign === 'center' ? 'center' : 'left',
    };
  });
  return {
    enabled: true, source: 'InvoiceDetail',
    x: M.x(tRect.left), y: M.y(headRect.top),
    width: round(Math.max(20, tRect.width * M.scale / PX_PER_MM)),
    rowHeight: round(Math.max(4, detail.row.getBoundingClientRect().height * M.scale / PX_PER_MM)),
    showHeader: true,
    headerHeight: round(Math.max(4, headRect.height * M.scale / PX_PER_MM)),
    showFooter: false, columns,
  };
}

/**
 * XSLT metnini Belge Dizaynı öğelerine + detay bandına çevirir.
 * @param {string} xsltText
 * @param {{ printWidthMm?: number, sampleXml?: string }} opts
 * @returns {{ elements: object[], detailBand: object|null, meta: object }}
 */
export function importXsltToLayout(xsltText, opts = {}) {
  if (!xsltText || !xsltText.trim()) throw new Error('XSLT dosyası boş.');
  const printWidthMm = opts.printWidthMm > 0 ? opts.printWidthMm : 190;
  const { host, root } = renderToHost(xsltText, opts.sampleXml);
  try {
    const detail = findDetail(root);
    const raw = collectRaw(root, detail?.table);
    const allRects = [...raw.texts.map((t) => t.rect), ...raw.decos.map((d) => d.rect)];
    if (detail) allRects.push(detail.table.getBoundingClientRect());
    if (!allRects.length) return { elements: [], detailBand: null, meta: { textCount: 0, decoCount: 0, hasDetail: false, detailCols: 0, boundCount: 0 } };
    const M = makeMapper(allRects, printWidthMm);

    const textEls = raw.texts.map((t) => textElement(t, M));
    let decoEls = decoElements(raw.decos, M);
    if (decoEls.length + textEls.length > MAX_ELEMENTS) decoEls = decoEls.slice(0, Math.max(0, MAX_ELEMENTS - textEls.length));
    const elements = [...decoEls, ...textEls];

    const detailBand = detail && detail.cells.length ? buildDetailBand(detail, M) : null;
    const boundCount = textEls.filter((e) => e.type === 'field').length + (detailBand ? detailBand.columns.filter((c) => c.fieldKey).length : 0);

    return {
      elements, detailBand,
      meta: {
        textCount: raw.texts.length, decoCount: raw.decos.length,
        hasDetail: !!detailBand, detailCols: detailBand ? detailBand.columns.length : 0, boundCount,
      },
    };
  } finally {
    if (host && host.parentNode) host.parentNode.removeChild(host);
  }
}
